import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ExcelJS odbija / \ ? * [ ] : u imenu lista (npr. broj dokumenta 12/2026 bi tiho oborio izvoz), a ime lista ne sme biti
// duže od 31 znaka; ime fajla ne sme imati / \ : * ? " < > |.
const safeSheetName = (name) => String(name).replace(/[\\/?*[\]:]/g, "-").substring(0, 31);
const safeFileName = (name) => String(name).replace(/[\\/:*?"<>|]/g, "-");

export default {
  methods: {
    setExcel() {
      this.$store.commit("SET_EXCEL"); // This likely toggles isExcelDownloading
    },
    async downloadExcel({ fileName = "export.xlsx", sheets = [] }) {
      try {
        const workbook = new ExcelJS.Workbook();

        for (const sheet of sheets) {
          const {
            name = "Sheet1",
            headers = [],
            items = [],
            title = "",
            subtitle = "",
            beforeTableHtml = "",
            footer = "",
          } = sheet;

          const exportHeaders = headers.filter(
            (header) => header.print !== false
          );
          const worksheet = workbook.addWorksheet(safeSheetName(name));

          // --- 1. Report meta/header rows ---
          let rowIdx = 1;
          // Date/time row
          const now = new Date();
          const dateStr =
            now.toLocaleDateString("sr-Latn-BA") +
            " " +
            now.toLocaleTimeString("sr-Latn-BA");
          worksheet.mergeCells(
            `A${rowIdx}:${String.fromCharCode(
              65 + exportHeaders.length - 1
            )}${rowIdx}`
          );
          worksheet.getCell(
            `A${rowIdx}`
          ).value = `Datum i vrijeme kreiranja izveštaja: ${dateStr}`;
          worksheet.getCell(`A${rowIdx}`).alignment = { horizontal: "left" };
          rowIdx++;

          // Title row
          worksheet.mergeCells(
            `A${rowIdx}:${String.fromCharCode(
              65 + exportHeaders.length - 1
            )}${rowIdx}`
          );
          worksheet.getCell(`A${rowIdx}`).value = title ? title.replace(/<[^>]+>/g, "") : "Izveštaj Obroci";
          worksheet.getCell(`A${rowIdx}`).font = { bold: true, size: 16 };
          worksheet.getCell(`A${rowIdx}`).alignment = { horizontal: "center" };
          rowIdx++;

          // Date range row
          worksheet.mergeCells(
            `A${rowIdx}:${String.fromCharCode(
              65 + exportHeaders.length - 1
            )}${rowIdx}`
          );
          worksheet.getCell(`A${rowIdx}`).value = subtitle ? subtitle.replace(/<[^>]+>/g, "") : "";
          worksheet.getCell(`A${rowIdx}`).font = { bold: true, size: 13 };
          worksheet.getCell(`A${rowIdx}`).alignment = { horizontal: "center" };
          rowIdx++;

          // Location/company row
          worksheet.mergeCells(
            `A${rowIdx}:${String.fromCharCode(
              65 + exportHeaders.length - 1
            )}${rowIdx}`
          );
          worksheet.getCell(`A${rowIdx}`).value = beforeTableHtml
            ? beforeTableHtml.replace(/<[^>]+>/g, "")
            : "";
          worksheet.getCell(`A${rowIdx}`).font = { bold: true };
          worksheet.getCell(`A${rowIdx}`).alignment = { horizontal: "center" };
          rowIdx++;

          // Empty row before table
          rowIdx++;

          // --- 2. Table header ---
          worksheet.spliceRows(
            rowIdx,
            0,
            exportHeaders.map((h) => h.text)
          );
          // Style header
          const headerRow = worksheet.getRow(rowIdx);
          headerRow.font = { bold: true };
          headerRow.alignment = { horizontal: "center", vertical: "middle" };
          headerRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFD3D3D3" },
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });

          // --- 3. Table data ---
          let tableStartRow = rowIdx + 1;
          let i = 0;
          let worksheetRow = tableStartRow;
          while (i < items.length) {
            const item = items[i];
            const nameColspan = (item.colspans || []).find(cs => cs.col === 'name');
            if (nameColspan && nameColspan.colspan > 1) {
              // Add all rows for this user
              for (let j = 0; j < nameColspan.colspan; j++) {
                const userRow = items[i + j];
                const row = [];
                exportHeaders.forEach((header) => {
                  let cellValue = "";
                  if (header.value && header.value.includes(".")) {
                    cellValue = header.value.split(".").reduce((obj, key) => {
                      return obj && obj[key] !== undefined ? obj[key] : "";
                    }, userRow);
                  } else {
                    cellValue = userRow[header.value];
                  }
                  row.push(cellValue === undefined || cellValue === null ? "" : cellValue);
                });
                worksheet.addRow(row);
              }
              // Merge the name column vertically for this user
              const colIdx = exportHeaders.findIndex(h => h.value === 'name');
              if (colIdx !== -1) {
                const colLetter = String.fromCharCode(65 + colIdx);
                const startRow = worksheetRow;
                const endRow = worksheetRow + nameColspan.colspan - 1;
                worksheet.mergeCells(`${colLetter}${startRow}:${colLetter}${endRow}`);
              }
              worksheetRow += nameColspan.colspan;
              i += nameColspan.colspan;
            } else {
              // Single row (user with no meals)
              const row = [];
              exportHeaders.forEach((header) => {
                let cellValue = "";
                if (header.value && header.value.includes(".")) {
                  cellValue = header.value.split(".").reduce((obj, key) => {
                    return obj && obj[key] !== undefined ? obj[key] : "";
                  }, item);
                } else {
                  cellValue = item[header.value];
                }
                row.push(cellValue === undefined || cellValue === null ? "" : cellValue);
              });
              worksheet.addRow(row);
              worksheetRow++;
              i++;
            }
          }

          // Style table cells
          for (let r = tableStartRow; r < tableStartRow + items.length; r++) {
            worksheet.getRow(r).eachCell((cell, colNumber) => {
              // Always apply border, even for empty cells
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
              // Set alignment based on header.align
              const header = headers[colNumber - 1];
              cell.alignment = {
                vertical: "middle",
                horizontal:
                  header && header.align === "start" ? "left" : "center",
                wrapText: true,
              };
              // Optionally, you can force empty cells to have a value (e.g., "")
              if (cell.value === undefined || cell.value === null) {
                cell.value = "";
              }
            });
          }

          // --- 5. Column widths ---
          exportHeaders.forEach((h, idx) => {
            worksheet.getColumn(idx + 1).width =
              h.text.length > 15 ? h.text.length + 5 : 15;
          });

          // --- 6. Footer row (if provided) ---
          if (footer) {
            rowIdx = tableStartRow + items.length + 1; // Add one empty row after table
            worksheet.mergeCells(
              `A${rowIdx}:${String.fromCharCode(
                65 + exportHeaders.length - 1
              )}${rowIdx}`
            );
            // Strip HTML tags from footer
            const footerText = footer.replace(/<[^>]+>/g, "");
            worksheet.getCell(`A${rowIdx}`).value = footerText;
            worksheet.getCell(`A${rowIdx}`).font = { size: 10 };
            worksheet.getCell(`A${rowIdx}`).alignment = { horizontal: "left", wrapText: true };
          }
        }

        // --- 7. Save file ---
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, safeFileName(fileName));
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        // this.$store.commit("SET_EXCEL_DOWNLOADING_STATUS", false); // Indicate download end/failure
      }
    },
  },
  computed: {
    isExcelDownloading() {
      return this.$store.state.isExcelDownloading;
    },
  },
};
